import wtBase, { WTEventParams } from '@clutter/wt';

const WT_EVENT_KEYS = new Set([
  'kind',
  'category',
  'action',
  'label',
  'value',
  'pageName',
  'container',
  'position',
  'objectType',
  'objectName',
  'metadata',
  'schema',
]);

export type PlatformWTEventParams = WTEventParams | Record<string, unknown>;

export type PlatformWTInitializeConfig = Parameters<typeof wtBase.initialize>[0] & {
  trackerUrl?: string;
};

export type PlatformWT = Omit<typeof wtBase, 'track' | 'set' | 'initialize'> & {
  track(kindOrParams: string | PlatformWTEventParams): void;
  set(defaults: PlatformWTEventParams): void;
  initialize(config: PlatformWTInitializeConfig): void;
};

const normalizeEventParams = (params: Record<string, unknown>): WTEventParams => {
  if ('schema' in params && params.schema) {
    return params as WTEventParams;
  }

  const result: WTEventParams = {};
  const metadata: Record<string, unknown> = {};

  if (params.metadata && typeof params.metadata === 'object') {
    Object.assign(metadata, params.metadata as Record<string, unknown>);
  }

  Object.entries(params).forEach(([key, value]) => {
    if (key === 'metadata') {
      return;
    }
    if (WT_EVENT_KEYS.has(key)) {
      (result as Record<string, unknown>)[key] = value;
    } else {
      metadata[key] = value;
    }
  });

  if (Object.keys(metadata).length > 0) {
    result.metadata = metadata;
  }

  return result;
};

const baseTrack = wtBase.track.bind(wtBase);
const baseSet = wtBase.set.bind(wtBase);
const baseInitialize = wtBase.initialize.bind(wtBase);

wtBase.track = ((kindOrParams: string | WTEventParams | Record<string, unknown>) => {
  if (typeof kindOrParams === 'string') {
    baseTrack(kindOrParams);
    return;
  }
  baseTrack(normalizeEventParams(kindOrParams as Record<string, unknown>));
}) as typeof wtBase.track;

// Keep identity defaults (customer_id, user_id, etc.) at the top level so @clutter/wt's
// shallow paramDefaults merge and addToQueue defaultParams behave like pre-wrapper code.
wtBase.set = ((defaults: WTEventParams | Record<string, unknown>) => {
  baseSet(defaults as WTEventParams);
}) as typeof wtBase.set;

wtBase.initialize = ((config: PlatformWTInitializeConfig) => {
  const { trackerUrl: _trackerUrl, ...rest } = config;
  baseInitialize(rest);
}) as typeof wtBase.initialize;

export const wt = wtBase as PlatformWT;
